// src/components/staff-time/StaffAvailabilityTab.jsx
import React, { useState, useEffect } from 'react';
import { Lock, Send, CheckCircle, Clock } from 'lucide-react';
import { db } from '../../services/firebase';
import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  collection,
  serverTimestamp
} from 'firebase/firestore';

const DAYS = [
  { id: 'monday',    label: 'Monday' },
  { id: 'tuesday',   label: 'Tuesday' },
  { id: 'wednesday', label: 'Wednesday' },
  { id: 'thursday',  label: 'Thursday' },
  { id: 'friday',    label: 'Friday' },
  { id: 'saturday',  label: 'Saturday' },
];

// Get Monday of the week — Sunday = start of NEXT Mon-Sat block
const getMondayOf = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  if (day === 0) {
    d.setDate(d.getDate() + 1); // Sunday → next Monday
  } else {
    d.setDate(d.getDate() - day + 1); // Mon–Sat → this Monday
  }
  d.setHours(0, 0, 0, 0);
  return d;
};

// Format Date → MM-DD-YYYY (used as Firestore doc key)
const toKey = (date) => {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const y = date.getFullYear();
  return `${m}-${d}-${y}`;
};

// Format Date → readable "Mon Apr 7"
const toLabel = (date) =>
  date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

// Convert HH:MM (24hr) → 12-hour display e.g. "2:00pm"
const to12Hour = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')}${ampm}`;
};

// Get the Monday date for a given week offset (0 = this week, 1 = next, 2 = week after)
const getWeekMonday = (offset) => {
  const monday = getMondayOf(new Date());
  monday.setDate(monday.getDate() + offset * 7);
  return monday;
};

// Is it past Monday midnight Pacific for this week?
const isWeekLocked = (weekMonday) => {
  // Convert now to Pacific time
  const nowPT = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })
  );
  const thisMondayPT = new Date(weekMonday);
  // Lock kicks in Tuesday 00:00 PT (i.e. after Monday 23:59)
  const lockDate = new Date(thisMondayPT);
  lockDate.setDate(lockDate.getDate() + 1); // Tuesday midnight
  return nowPT >= lockDate;
};

// Days until next Monday midnight PT
const getDeadlineText = () => {
  const nowPT = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })
  );
  // If today is past Monday, next deadline is NEXT Monday
  if (nowPT.getDay() !== 1 || nowPT.getHours() >= 0) {
    // Find the upcoming Monday
    const upcoming = getMondayOf(new Date());
    if (nowPT > upcoming) {
      upcoming.setDate(upcoming.getDate() + 7);
    }
    const diff = upcoming - nowPT;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${hours}h until Monday deadline`;
    if (hours > 0) return `${hours} hours until Monday deadline`;
    return 'Deadline is tonight at midnight PT';
  }
  return 'Deadline is tonight at midnight PT';
};

const createDefaultWeek = () => {
  const days = {};
  DAYS.forEach(({ id }) => {
    days[id] = { available: false, startTime: '', endTime: '' };
  });
  return days;
};

// Get the week key for N weeks before a given key (format: MM-DD-YYYY)
const getPrevWeekKey = (key) => {
  const [m, d, y] = key.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() - 7);
  const pm = String(date.getMonth() + 1).padStart(2, '0');
  const pd = String(date.getDate()).padStart(2, '0');
  const py = date.getFullYear();
  return `${pm}-${pd}-${py}`;
};

const StaffAvailabilityTab = ({ staff }) => {
  const [selectedWeekOffset, setSelectedWeekOffset] = useState(0);
  const [weeksData, setWeeksData] = useState({}); // { weekKey: { days, locked, submittedAt } }
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [changeRequest, setChangeRequest] = useState(null); // { dayId } or null
  const [changeReason, setChangeReason] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [error, setError] = useState('');

  // Build 4 week options (current + 3 ahead)
  const weeks = [0, 1, 2, 3].map((offset) => {
    const monday = getWeekMonday(offset);
    const saturday = new Date(monday);
    saturday.setDate(monday.getDate() + 5);
    return {
      offset,
      monday,
      key: toKey(monday),
      label: offset === 0
        ? `This Week (${toLabel(monday)} – ${toLabel(saturday)})`
        : offset === 1
        ? `Next Week (${toLabel(monday)} – ${toLabel(saturday)})`
        : `${toLabel(monday)} – ${toLabel(saturday)}`,
    };
  });

  const currentWeek = weeks[selectedWeekOffset];
  const weekKey = currentWeek.key;
  const locked = isWeekLocked(currentWeek.monday);
  const weekData = weeksData[weekKey] || { days: createDefaultWeek(), submittedAt: null };
  const days = weekData.days;

  // Load availability for a week from Firestore
  // If no data exists, copy from previous week (auto-carry-forward)
  const loadWeek = async (key) => {
    if (weeksData[key]) return; // already loaded
    try {
      const ref = doc(db, 'staff_weekly_availability', staff.id, 'weeks', key);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setWeeksData(prev => ({ ...prev, [key]: snap.data() }));
      } else {
        // No data for this week — try to copy from previous week
        const prevKey = getPrevWeekKey(key);
        const prevRef = doc(db, 'staff_weekly_availability', staff.id, 'weeks', prevKey);
        const prevSnap = await getDoc(prevRef);
        if (prevSnap.exists() && prevSnap.data().days) {
          // Copy previous week's days but mark as carried forward (not yet submitted)
          setWeeksData(prev => ({
            ...prev,
            [key]: {
              days: prevSnap.data().days,
              submittedAt: null,
              locked: false,
              carriedForwardFrom: prevKey,
            }
          }));
        } else {
          // No previous week either — use empty default
          setWeeksData(prev => ({
            ...prev,
            [key]: { days: createDefaultWeek(), submittedAt: null, locked: false }
          }));
        }
      }
    } catch (err) {
      console.error('Error loading week:', err);
      setWeeksData(prev => ({
        ...prev,
        [key]: { days: createDefaultWeek(), submittedAt: null, locked: false }
      }));
    }
  };

  useEffect(() => {
    loadWeek(weekKey);
    setChangeRequest(null);
    setChangeReason('');
    setRequestSuccess(false);
    setError('');
    setSaveSuccess(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekKey]);

  const updateDay = (dayId, field, value) => {
    setWeeksData(prev => ({
      ...prev,
      [weekKey]: {
        ...prev[weekKey],
        days: {
          ...prev[weekKey]?.days,
          [dayId]: {
            ...(prev[weekKey]?.days?.[dayId] || { available: false, startTime: '', endTime: '' }),
            [field]: value,
            // If toggling off, clear times
            ...(field === 'available' && !value ? { startTime: '', endTime: '' } : {})
          }
        }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const ref = doc(db, 'staff_weekly_availability', staff.id, 'weeks', weekKey);
      await setDoc(ref, {
        days,
        submittedAt: serverTimestamp(),
        locked: false,
        staffId: staff.id,
        staffName: `${staff.firstName} ${staff.lastName}`,
        weekStart: weekKey,
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitChangeRequest = async () => {
    if (!changeReason.trim()) {
      setError('Please enter a reason for the change request.');
      return;
    }
    setSubmittingRequest(true);
    setError('');
    try {
      await addDoc(collection(db, 'availability_change_requests'), {
        staffId: staff.id,
        staffName: `${staff.firstName} ${staff.lastName}`,
        weekStart: weekKey,
        dayId: changeRequest.dayId,
        dayLabel: DAYS.find(d => d.id === changeRequest.dayId)?.label,
        reason: changeReason.trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setRequestSuccess(true);
      setChangeRequest(null);
      setChangeReason('');
    } catch (err) {
      console.error('Change request error:', err);
      setError('Failed to submit request. Please try again.');
    } finally {
      setSubmittingRequest(false);
    }
  };

  return (
    <div style={{ padding: '1rem', maxWidth: '500px', margin: '0 auto' }}>

      {/* Deadline Banner */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.75rem 1rem',
        backgroundColor: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: '8px',
        marginBottom: '1rem',
        fontSize: '0.8rem',
        color: '#92400e',
      }}>
        <Clock size={14} style={{ flexShrink: 0 }} />
        <span>{getDeadlineText()} — updates lock after Monday midnight PT</span>
      </div>

      {/* Week Selector */}
      <div style={{ marginBottom: '1.25rem' }}>
        <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '0.4rem' }}>
          Select Week
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {weeks.map(w => (
            <button
              key={w.offset}
              onClick={() => setSelectedWeekOffset(w.offset)}
              style={{
                padding: '0.65rem 1rem',
                textAlign: 'left',
                border: '2px solid',
                borderColor: selectedWeekOffset === w.offset ? '#3b82f6' : '#e5e7eb',
                borderRadius: '8px',
                backgroundColor: selectedWeekOffset === w.offset ? '#eff6ff' : 'white',
                color: selectedWeekOffset === w.offset ? '#1e40af' : '#374151',
                fontSize: '0.85rem',
                fontWeight: selectedWeekOffset === w.offset ? '600' : '400',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {w.label}
              {isWeekLocked(w.monday) && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                  🔒 Locked
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Lock Notice */}
      {locked && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1rem',
          backgroundColor: '#f1f5f9',
          border: '1px solid #cbd5e1',
          borderRadius: '8px',
          marginBottom: '1rem',
          fontSize: '0.8rem',
          color: '#475569',
        }}>
          <Lock size={14} style={{ flexShrink: 0 }} />
          <span>This week is locked. To request a change, tap <strong>Request Change</strong> on the day.</span>
        </div>
      )}

      {/* Day Grid */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.25rem' }}>
        {DAYS.map(({ id, label }) => {
          const day = days[id] || { available: false, startTime: '', endTime: '' };
          return (
            <div
              key={id}
              style={{
                border: '1.5px solid',
                borderColor: day.available ? '#3b82f6' : '#e5e7eb',
                borderRadius: '10px',
                backgroundColor: day.available ? '#eff6ff' : '#f9fafb',
                overflow: 'hidden',
                transition: 'all 0.15s',
              }}
            >
              {/* Day Header Row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.65rem 1rem',
              }}>
                <span style={{
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  color: day.available ? '#1e40af' : '#6b7280',
                  minWidth: '90px',
                }}>
                  {label}
                </span>

                {locked ? (
                  // Locked state — show current value + request change button
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{
                      fontSize: '0.8rem',
                      color: day.available ? '#1d4ed8' : '#9ca3af',
                      fontWeight: '500',
                    }}>
                      {day.available
                        ? `✅ ${to12Hour(day.startTime)}${day.startTime && day.endTime ? ' – ' : ''}${to12Hour(day.endTime)}`
                        : '— Unavailable'}
                    </span>
                    <button
                      onClick={() => {
                        setChangeRequest({ dayId: id });
                        setRequestSuccess(false);
                        setError('');
                      }}
                      style={{
                        padding: '0.3rem 0.6rem',
                        fontSize: '0.75rem',
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Request Change
                    </button>
                  </div>
                ) : (
                  // Editable toggle
                  <button
                    onClick={() => updateDay(id, 'available', !day.available)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: '0.35rem 0.8rem',
                      border: '1.5px solid',
                      borderColor: day.available ? '#3b82f6' : '#d1d5db',
                      borderRadius: '20px',
                      backgroundColor: day.available ? '#3b82f6' : 'white',
                      color: day.available ? 'white' : '#6b7280',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {day.available ? '✓ Available' : 'Unavailable'}
                  </button>
                )}
              </div>

              {/* Time Row — only show if available and not locked */}
              {day.available && !locked && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0 1rem 0.65rem',
                }}>
                  <span style={{ fontSize: '0.8rem', color: '#6b7280', minWidth: '28px' }}>From</span>
                  <input
                    type="time"
                    value={day.startTime || ''}
                    onChange={(e) => updateDay(id, 'startTime', e.target.value)}
                    style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      padding: '0.3rem 0.5rem',
                      fontSize: '0.85rem',
                      color: '#1f2937',
                      backgroundColor: 'white',
                      flex: 1,
                    }}
                  />
                  <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>to</span>
                  <input
                    type="time"
                    value={day.endTime || ''}
                    onChange={(e) => updateDay(id, 'endTime', e.target.value)}
                    style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      padding: '0.3rem 0.5rem',
                      fontSize: '0.85rem',
                      color: '#1f2937',
                      backgroundColor: 'white',
                      flex: 1,
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          fontSize: '0.85rem',
          marginBottom: '1rem',
        }}>
          {error}
        </div>
      )}

      {/* Save Button — only when not locked */}
      {!locked && (
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: '100%',
            padding: '1rem',
            backgroundColor: saving ? '#9ca3af' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'background-color 0.2s',
          }}
        >
          {saving ? (
            '⏳ Saving...'
          ) : saveSuccess ? (
            <><CheckCircle size={18} /> Saved!</>
          ) : (
            <><Send size={18} /> Save Availability</>
          )}
        </button>
      )}

      {/* Carry-forward notice */}
      {weekData.carriedForwardFrom && !weekData.submittedAt && (
        <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '0.65rem 1rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          📋 Copied from previous week — review and save to confirm your availability.
        </div>
      )}

      {/* Change Request Modal */}
      {changeRequest && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem',
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            width: '100%',
            maxWidth: '400px',
          }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: '700', color: '#1f2937' }}>
              Request a Change
            </h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#6b7280' }}>
              For <strong>{DAYS.find(d => d.id === changeRequest.dayId)?.label}</strong> — week of {weekKey}. Your manager will review this request.
            </p>

            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '0.4rem' }}>
              Reason *
            </label>
            <textarea
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="e.g. Doctor appointment, family emergency..."
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1.5px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '0.9rem',
                resize: 'vertical',
                boxSizing: 'border-box',
                marginBottom: '1rem',
                fontFamily: 'inherit',
              }}
            />

            {error && (
              <div style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => { setChangeRequest(null); setChangeReason(''); setError(''); }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: 'white',
                  border: '1.5px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  color: '#374151',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitChangeRequest}
                disabled={submittingRequest}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: submittingRequest ? '#9ca3af' : '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: submittingRequest ? 'not-allowed' : 'pointer',
                }}
              >
                {submittingRequest ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Success Banner */}
      {requestSuccess && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem 1rem',
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          color: '#15803d',
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}>
          <CheckCircle size={16} />
          Change request submitted — your manager will review it shortly.
        </div>
      )}
    </div>
  );
};

export default StaffAvailabilityTab;